import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import api, { errorMessage } from "../lib/api";
import { suggestedRoles } from "../lib/skills";
import SuggestedRoleHint from "../components/SuggestedRoleHint";
import { roleBadgeClass } from "../lib/roles";
import { getInitials, avatarColorClass } from "../lib/avatar";
import UserDetailSheet from "../components/UserDetailSheet";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ResponsivePopover as Popover, ResponsivePopoverTrigger as PopoverTrigger, ResponsivePopoverContent as PopoverContent } from "../components/ui/responsive-popover";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, MoreHorizontal, Eye, Pencil, Shield, Power, UserPlus, IdCard,
  ChevronLeft, ChevronRight, SlidersHorizontal, X,
} from "lucide-react";

const PAGE_SIZE = 8;

// Collaborator fiche status of an account, only when the module is on (the server then adds `collaborator`: null or {id, reference}).
function FicheBadge({ u, moduleOn, onCreate }) {
  if (!moduleOn) return null;
  if (u.collaborator) {
    return (
      <Link to={`/collaborateurs/${u.collaborator.id}`} onClick={(e) => e.stopPropagation()} data-testid={`fiche-badge-${u.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-2xs font-medium hover:bg-emerald-100">
        <IdCard size={11} /> Fiche {u.collaborator.reference}
      </Link>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span data-testid={`no-fiche-badge-${u.id}`} className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-2xs font-medium">
        Sans fiche
      </span>
      {onCreate && u.is_active !== false && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onCreate(); }} data-testid={`btn-create-fiche-${u.id}`}
          className="inline-flex items-center gap-1 text-2xs font-medium text-jamora hover:underline">
          <UserPlus size={12} /> Créer la fiche
        </button>
      )}
    </span>
  );
}

function formatLastLogin(iso) {
  if (!iso) return "Jamais connecté";
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const time = date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (date.toDateString() === now.toDateString()) return `Il y a ${diffHours} heure${diffHours > 1 ? "s" : ""}`;
  if (date.toDateString() === yesterday.toDateString()) return `Hier à ${time}`;
  if (diffDays < 30) return `Il y a ${diffDays} jour${diffDays > 1 ? "s" : ""}`;
  return date.toLocaleDateString("fr-FR");
}

export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const moduleOn = !!user?.features?.collaborators && user?.role === "admin";
  const canCreate = hasPermission(user, "users:create");
  const canEdit = hasPermission(user, "users:edit");
  const canAssignRole = hasPermission(user, "users:assign_role");
  const canActivate = hasPermission(user, "users:activate");
  const canCreateFiche = user?.role === "admin" && moduleOn;
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [viewUser, setViewUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editRoleOnly, setEditRoleOnly] = useState(false);
  const [editForm, setEditForm] = useState({ role: "", is_active: true, whatsapp_number: "" });
  const [jobFunctions, setJobFunctions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: u }, { data: r }] = await Promise.all([api.get("/users"), api.get("/users/role-options")]);
      setUsers(u);
      setRoles(r);
    } catch (e) {
      // A 403 here means the account lost the right while the page was open: the rights are re-read and the page closes.
      // Say so rather than report a technical failure.
      if (e.response?.status === 403) toast.info("Vos droits ont changé : cette page ne vous est plus accessible.");
      else toast.error("Erreur de chargement");
    }
  };
  useEffect(() => {
    load();
  }, []);
  // Job functions, to show the role a linked fiche's functions suggest (admin only: `collaborator` is admin-only).
  useEffect(() => {
    if (!moduleOn) return;
    api.get("/job-functions").then(({ data }) => setJobFunctions(data.items)).catch(() => setJobFunctions([]));
  }, [moduleOn]);

  // Global search lands here with { openUser: id }: open that account's drawer once the list is loaded.
  useEffect(() => {
    const wanted = location.state?.openUser;
    if (!wanted || !users) return;
    const found = users.find((x) => x.id === wanted);
    if (found) setViewUser(found);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, users, location.pathname, navigate]);

  const roleLabel = useCallback((name) => roles.find((r) => r.name === name)?.label || name, [roles]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (statusFilter === "active" && u.is_active === false) return false;
      if (statusFilter === "inactive" && u.is_active !== false) return false;
      if (q) {
        const haystack = `${u.name} ${u.email} ${roleLabel(u.role)}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, statusFilter, roleLabel]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  const hasActiveFilters = roleFilter !== "all" || statusFilter !== "all";
  const resetFilters = () => {
    setRoleFilter("all");
    setStatusFilter("all");
  };
  // Mobile-only removable chips: desktop already shows the active value
  // directly inside each visible Select.
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (roleFilter !== "all") chips.push({ id: "role", label: roleLabel(roleFilter), onRemove: () => setRoleFilter("all") });
    if (statusFilter !== "all") {
      chips.push({ id: "status", label: statusFilter === "active" ? "Actif" : "Désactivé", onRemove: () => setStatusFilter("all") });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter, roles]);

  if (!hasPermission(user, "users:view")) return <Navigate to="/pipeline" replace />;

  const toggleActive = async (target) => {
    setUpdatingId(target.id);
    try {
      const { data } = await api.patch(`/users/${target.id}`, { is_active: !target.is_active });
      setUsers((prev) => prev.map((u) => (u.id === target.id ? { ...u, is_active: data.is_active } : u)));
      toast.success(data.is_active ? "Compte réactivé" : "Compte désactivé");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setUpdatingId(null);
    }
  };

  const openEdit = (target, roleOnly) => {
    setEditUser(target);
    setEditRoleOnly(roleOnly);
    setEditForm({ role: target.role, is_active: target.is_active !== false, whatsapp_number: target.whatsapp_number || "" });
  };

  const saveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const payload = editRoleOnly ? { role: editForm.role } : {
        ...(canAssignRole && editForm.role !== editUser.role ? { role: editForm.role } : {}),
        ...(canActivate && editForm.is_active !== (editUser.is_active !== false) ? { is_active: editForm.is_active } : {}),
        ...(canEdit ? { whatsapp_number: editForm.whatsapp_number.trim() } : {}),
      };
      const { data } = await api.patch(`/users/${editUser.id}`, payload);
      setUsers((prev) => prev.map((u) => (u.id === editUser.id ? { ...u, ...data } : u)));
      toast.success("Utilisateur mis à jour");
      setEditUser(null);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (users === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-jamora" />
      </div>
    );

  // Same RBAC-respecting action list for desktop's row menu and the mobile
  // card's; the "own account" restriction (toggleActive disabled for isSelf)
  // lives inside this one definition, so the two can't drift.
  const createFiche = (u) => navigate("/collaborateurs/nouveau", { state: { fromUser: { id: u.id, name: u.name, email: u.email, whatsapp: u.whatsapp_number || "" } } });

  // Same rights as the row menu, as buttons at the foot of the detail drawer. Opening another surface closes the drawer first.
  const detailActions = (u) => {
    const isSelf = u.id === user.id;
    const manageable = user.role === "admin" || u.role !== "admin";
    const then = (fn) => () => { setViewUser(null); fn(); };
    return (
      <>
        {canEdit && manageable && <Button variant="outline" size="sm" onClick={then(() => openEdit(u, false))} data-testid="user-detail-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>}
        {canAssignRole && manageable && <Button variant="outline" size="sm" onClick={then(() => openEdit(u, true))}><Shield size={14} className="mr-1.5" />Changer le rôle</Button>}
        {moduleOn && !u.collaborator && u.is_active !== false && canCreateFiche && (
          <Button variant="outline" size="sm" onClick={then(() => createFiche(u))}><UserPlus size={14} className="mr-1.5" />Créer la fiche collaborateur</Button>
        )}
        {canActivate && manageable && !isSelf && (
          <Button variant="outline" size="sm" className={u.is_active !== false ? "text-red-700" : ""} onClick={then(() => toggleActive(u))} data-testid="user-detail-toggle">
            <Power size={14} className="mr-1.5" />{u.is_active !== false ? "Désactiver le compte" : "Réactiver le compte"}
          </Button>
        )}
      </>
    );
  };

  const userActionItems = (u) => {
    const isSelf = u.id === user.id;
    const active = u.is_active !== false;
    return (
      <>
        <DropdownMenuItem
          onClick={() => (isSelf ? navigate("/profile") : setViewUser(u))}
          data-testid={`action-view-${u.id}`}
        >
          <Eye size={14} className="mr-2" /> Voir le détail
        </DropdownMenuItem>
        {canEdit && (user.role === "admin" || u.role !== "admin") && <DropdownMenuItem onClick={() => openEdit(u, false)} data-testid={`action-edit-${u.id}`}>
          <Pencil size={14} className="mr-2" /> Modifier l'utilisateur
        </DropdownMenuItem>}
        {canAssignRole && (user.role === "admin" || u.role !== "admin") && <DropdownMenuItem onClick={() => openEdit(u, true)} data-testid={`action-role-${u.id}`}>
          <Shield size={14} className="mr-2" /> Changer le rôle
        </DropdownMenuItem>}
        {moduleOn && (
          u.collaborator ? (
            <DropdownMenuItem onClick={() => navigate(`/collaborateurs/${u.collaborator.id}`)} data-testid={`action-fiche-${u.id}`}>
              <IdCard size={14} className="mr-2" /> Voir la fiche collaborateur
            </DropdownMenuItem>
          ) : active && canCreateFiche && (
            <DropdownMenuItem
              onClick={() => createFiche(u)}
              data-testid={`action-create-fiche-${u.id}`}
            >
              <UserPlus size={14} className="mr-2" /> Créer la fiche collaborateur
            </DropdownMenuItem>
          )
        )}
        {canActivate && (user.role === "admin" || u.role !== "admin") && <DropdownMenuSeparator />}
        {canActivate && (user.role === "admin" || u.role !== "admin") && <DropdownMenuItem
          onClick={() => toggleActive(u)}
          disabled={isSelf}
          title={isSelf ? "Vous ne pouvez pas désactiver votre propre compte" : undefined}
          data-testid={`action-toggle-active-${u.id}`}
        >
          <Power size={14} className="mr-2" /> {active ? "Désactiver le compte" : "Réactiver le compte"}
        </DropdownMenuItem>}
      </>
    );
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Utilisateurs</h1>
          <p className="hidden md:block text-sm text-slate-500">Gérez les membres de votre équipe et leurs accès.</p>
          <p className="md:hidden text-sm text-slate-500">{filtered.length} utilisateur{filtered.length === 1 ? "" : "s"}</p>
        </div>
        {/* Unlike Tasks/Prospects, user creation has no header "+" shortcut,
            so mobile keeps its own icon-only trigger here (same /users/nouveau
            page). */}
        {canCreate && <Button
          data-testid="btn-users-new"
          onClick={() => navigate("/users/nouveau")}
          className="px-3 md:px-4"
        >
          <Plus size={16} className="md:mr-1.5" /> <span className="hidden md:inline">Nouvel utilisateur</span>
        </Button>}
      </div>

      {/* Compact mobile filter bar: search + a single "Filtres" popover
          reusing the exact same roleFilter/statusFilter state as desktop's
          always-visible selects below. */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              data-testid="input-users-search"
              placeholder="Rechercher un utilisateur (nom, email, rôle…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="hidden md:flex gap-3">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="select-role-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="select-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="inactive">Désactivé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden shrink-0" aria-label="Filtres" data-testid="btn-users-filters-toggle">
                <SlidersHorizontal size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-3 md:hidden">
              <div className="text-sm font-semibold text-slate-900">Filtres</div>
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-slate-500">Rôle</div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger data-testid="select-role-filter-mobile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les rôles</SelectItem>
                    {roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-slate-500">Statut</div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="select-status-filter-mobile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="inactive">Désactivé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline" size="sm" className="w-full"
                onClick={resetFilters} disabled={!hasActiveFilters} data-testid="btn-reset-filters-mobile"
              >
                Réinitialiser
              </Button>
            </PopoverContent>
          </Popover>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="md:hidden flex flex-wrap gap-1.5">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.id} type="button" onClick={chip.onRemove}
                data-testid={`chip-${chip.id}`}
                className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1.5 py-1 hover:bg-slate-200"
              >
                {chip.label} <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Flattened on mobile (no border/shadow/rounded/bg): each user is
          already its own bordered card there. */}
      <Card className="p-0 overflow-hidden border-0 shadow-none rounded-none bg-transparent md:rounded-xl md:border md:shadow md:bg-card">
        {filtered.length === 0 ? (
          <>
            <div className="hidden md:block p-12 text-center text-slate-500 text-sm">Aucun utilisateur ne correspond à ces critères.</div>
            <div className="md:hidden text-center py-12 px-4">
              <p className="text-sm text-slate-500">Aucun utilisateur ne correspond à ces critères.</p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" className="mt-3" onClick={resetFilters} data-testid="btn-reset-filters-empty">
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rôle</TableHead>
                {moduleOn && <TableHead>Fiche collaborateur</TableHead>}
                <TableHead>Statut</TableHead>
                <TableHead>Dernière connexion</TableHead>
                <TableHead>Créé le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((u) => {
                const isSelf = u.id === user.id;
                const active = u.is_active !== false;
                return (
                  <TableRow key={u.id} data-testid={`row-user-${u.id}`} className="cursor-pointer"
                    onClick={() => (isSelf ? navigate("/profile") : setViewUser(u))}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColorClass(u.id)}`}>
                          {getInitials(u.name, u.email)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={(e) => { e.stopPropagation(); if (isSelf) navigate("/profile"); else setViewUser(u); }}
                              className="text-left font-medium text-sm text-slate-900 hover:text-jamora hover:underline" data-testid={`open-user-${u.id}`}>{u.name}</button>
                            {isSelf && <Badge variant="secondary" className="text-2xs">Vous</Badge>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`border-transparent text-2xs font-medium ${roleBadgeClass(u.role)}`}>
                        {roleLabel(u.role)}
                      </Badge>
                    </TableCell>
                    {moduleOn && <TableCell onClick={(e) => e.stopPropagation()}><FicheBadge u={u} moduleOn={moduleOn} onCreate={canCreateFiche ? () => createFiche(u) : null} /></TableCell>}
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-red-500"}`} />
                        <span className={active ? "text-emerald-700" : "text-red-600"}>{active ? "Actif" : "Désactivé"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatLastLogin(u.last_login)}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "Inconnue"}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={updatingId === u.id}
                            aria-label={`Actions pour ${u.name || u.email}`}
                            data-testid={`btn-user-actions-${u.id}`}
                          >
                            {updatingId === u.id ? <Loader2 size={16} className="animate-spin" /> : <MoreHorizontal size={16} />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">{userActionItems(u)}</DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>

          <div className="md:hidden p-2 space-y-2">
            {paged.map((u) => (
              <UserCard
                key={u.id} u={u} currentUser={user} roleLabel={roleLabel}
                updatingId={updatingId} actionItems={userActionItems(u)} moduleOn={moduleOn} onCreateFiche={canCreateFiche ? () => createFiche(u) : null}
                onOpen={() => (u.id === user.id ? navigate("/profile") : setViewUser(u))}
              />
            ))}
          </div>
          </>
        )}
      </Card>

      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filtered.length)} sur {filtered.length} utilisateur{filtered.length > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
              aria-label="Page précédente"
              data-testid="btn-users-prev-page"
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="w-8 h-8 rounded-md bg-jamora text-white flex items-center justify-center text-xs font-medium">
              {currentPage}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
              aria-label="Page suivante"
              data-testid="btn-users-next-page"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}

      <UserDetailSheet userRow={viewUser} onClose={() => setViewUser(null)} actions={viewUser && detailActions(viewUser)} />

      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent>
          {editUser && (
            <>
              <DialogHeader>
                <DialogTitle>{editRoleOnly ? `Changer le rôle de ${editUser.name}` : `Modifier ${editUser.name}`}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {canAssignRole && <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Rôle</label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roles.filter((r) => r.assignable).map((r) => (
                        <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <SuggestedRoleHint suggestions={suggestedRoles(jobFunctions, editUser.collaborator?.job_function_ids)} currentRole={editUser.role} selectedRole={editForm.role}
                    assignable={roles.filter((r) => r.assignable).map((r) => r.name)} onUse={(role) => setEditForm((f) => ({ ...f, role }))} />
                </div>}
                {!editRoleOnly && canActivate && (
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-900">Compte actif</div>
                      <div className="text-xs text-slate-500">Un compte désactivé ne peut plus se connecter.</div>
                    </div>
                    <Switch
                      checked={editForm.is_active}
                      onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: v }))}
                      disabled={editUser.id === user.id}
                      data-testid="edit-user-active"
                    />
                  </div>
                )}
                {!editRoleOnly && canEdit && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Numéro WhatsApp</label>
                    <Input
                      placeholder="+221 77 123 45 67"
                      value={editForm.whatsapp_number}
                      onChange={(e) => setEditForm((f) => ({ ...f, whatsapp_number: e.target.value }))}
                      data-testid="edit-user-whatsapp"
                    />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditUser(null)}>Annuler</Button>
                <Button
                  onClick={saveEdit}
                  disabled={saving}
                  data-testid="btn-save-edit-user"
                >
                  {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  Enregistrer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Mobile user card: tapping it opens the same detail drawer as the "⋯" menu.
// The "⋯" trigger stops propagation so it never also triggers that. Status
// reads "Désactivé", matching the page's "Désactiver le compte" action.
function UserCard({ u, currentUser, roleLabel, updatingId, actionItems, onOpen, moduleOn, onCreateFiche }) {
  const isSelf = u.id === currentUser.id;
  const active = u.is_active !== false;
  return (
    <div
      data-testid={`user-card-${u.id}`}
      className={`rounded-lg border border-slate-100 bg-white px-3 py-2 active:bg-slate-50 transition ${active ? "" : "opacity-70"}`}
      onClick={onOpen}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColorClass(u.id)}`}>
          {getInitials(u.name, u.email)}
        </div>
        <div className="min-w-0 flex-1 font-medium text-sm text-slate-900 break-words">{u.name}</div>
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500"
                disabled={updatingId === u.id}
                data-testid={`btn-user-actions-mobile-${u.id}`}
              >
                {updatingId === u.id ? <Loader2 size={16} className="animate-spin" /> : <MoreHorizontal size={16} />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">{actionItems}</DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="pl-11 mt-0.5 text-xs text-slate-500 break-words">{u.email}</div>
      {moduleOn && <div className="pl-11 mt-1"><FicheBadge u={u} moduleOn={moduleOn} onCreate={onCreateFiche} /></div>}

      <div className="pl-11 mt-1 flex items-center justify-between gap-2">
        <Badge variant="outline" className={`border-transparent text-2xs font-medium ${roleBadgeClass(u.role)}`}>
          {roleLabel(u.role)}
        </Badge>
        <div className="flex items-center gap-1.5 text-xs shrink-0">
          <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-red-500"}`} />
          <span className={active ? "text-emerald-700" : "text-red-600"}>{active ? "Actif" : "Désactivé"}</span>
        </div>
      </div>

      {isSelf && <div className="pl-11 mt-1"><Badge variant="secondary" className="text-2xs">Vous</Badge></div>}
    </div>
  );
}
